/* ===== world/dungeon.js — 방이 여러 개인 던전 · 걸어서 닿는가(통행 보수) ===== */
import { factory as Factory } from '../ctx.js';
import { clamp } from '../../engine/core/math.js';
import { mixin } from '../../engine/core/mixin.js';
import { BoxSet } from '../../engine/procgen/cells.js';
import { COLUMN_WALL, T, TILE_DEF } from '../data.js';
import { RUIN_PLANS } from '../data/ruins.js';
import { World } from '../world.js';
/* world.js 의 World 에서 나눈 조각 — 읽히는 순간 World.prototype 에 붙는다(main.js 가 world.js 다음에 읽는다). */

export const WorldDungeon: Bag & ThisType<World> = {

  /* ================= 방이 여러 개인 던전 ================= */
  bspSplit(x: any, y: any, w: any, h: any, depth: any, minW: any, minH: any, rng: any, out: any) {
    const canH = h >= minH * 2 + 1, canV = w >= minW * 2 + 1;
    if (depth <= 0 || (!canH && !canV)) { out.push({ x, y, w, h }); return; }
    /* ★ 가로가 세로의 1.6배가 안 되면 가로로 잘라 납작하게 만든다. */
    const horiz = canH && (!canV || (h * 1.6 > w ? rng.chance(0.95) : rng.chance(0.15)));
    if (horiz) {
      const cut = rng.int(minH, h - minH - 1);
      this.bspSplit(x, y, w, cut, depth - 1, minW, minH, rng, out);
      this.bspSplit(x, y + cut, w, h - cut, depth - 1, minW, minH, rng, out);
    } else {
      const cut = rng.int(minW, w - minW - 1);
      this.bspSplit(x, y, cut, h, depth - 1, minW, minH, rng, out);
      this.bspSplit(x + cut, y, w - cut, h, depth - 1, minW, minH, rng, out);
    }
  },

  /** 두 방 사이 공유 벽에 통로를 뚫는다. */
  _linkRooms(a: any, b: any, floor: number) {
    const ax1 = a.x + a.w, ay1 = a.y + a.h, bx1 = b.x + b.w, by1 = b.y + b.h;
    /* ★ 옆으로 붙었는지 볼 때 겹치는 높이가 없으면 **돌려주지 말고 아래(위아래로 붙었나)로 넘어간다.** */
    const side = (ax1 === b.x || bx1 === a.x) &&
      Math.min(ay1, by1) - 2 >= Math.max(a.y, b.y) + 1;
    if (side) {                                              // 세로 벽을 공유
      const wx = ax1 === b.x ? ax1 - 1 : bx1 - 1;
      const y0 = Math.max(a.y, b.y) + 1, y1 = Math.min(ay1, by1) - 2;
      if (y1 < y0) return false;
      // 바닥에 붙여 뚫어야 걸어서 지나갈 수 있다
      const dy = y1;
      // 낡은 유적은 세 줄 — 두 줄 문은 걷는 줄 위가 막혀 선 채로 못 지나갔다(보수가 못 고친 씨앗이 있었다)
      for (let k = 0; k < (this._tallLink ? 3 : 2); k++) { this.set(wx, dy - k, T.AIR); this.set(wx + 1, dy - k, T.AIR); this.set(wx - 1, dy - k, T.AIR); }
      return true;
    }
    if (ay1 === b.y || by1 === a.y) {                       // 가로 벽을 공유 (위아래)
      const wy = ay1 === b.y ? ay1 - 1 : by1 - 1;
      /* ★ 오른쪽 끝을 **네 칸** 물린다. */
      const x0 = Math.max(a.x, b.x) + 2, x1 = Math.min(ax1, bx1) - 4;
      if (x1 < x0) return false;
      const dx = (x0 + x1) >> 1;
      for (let k = -1; k <= 1; k++) { this.set(dx + k, wy, T.AIR); this.set(dx + k, wy + 1, T.AIR); }
      /* ★ 윗방 **바닥 타일**(wy-1)도 뚫어 발판으로 바꾼다. */
      for (let k = -1; k <= 1; k++) this.set(dx + k, wy - 1, T.PLATFORM);
      // 위층으로 올라갈 발판 사다리 — 높은 방은 아랫방 바닥에서 뛰어 닿는 데(걷는 줄 -2)까지 내린다
      const lo = ay1 === b.y ? b : a, end = this._tallLink ? Math.max(wy + 4, lo.y + lo.h - 5) : wy + 4;
      for (let yy = wy + 1; yy <= end && yy < by1 + a.h; yy++) this.set(dx, yy, T.PLATFORM);
      this.set(dx, wy, T.PLATFORM);
      return true;
    }
    return false;
  },

  /** 방 묶음 던전을 짓고 방 목록을 돌려준다 */
  carveDungeon(cfg: any) {
    const { x0, y0, w, h, wall, floor, bg, rng } = cfg;
    const minW = cfg.minW || 11, minH = cfg.minH || 9;
    /* 구조 경향(cfg.style) — wide: 낮고 긴 갱도 · sparse: 큰 방이 암반을 사이에 두고 듬성듬성(굴로 잇는다) · maze: 잔방 미로 · tall: 높은 방. 사연: docs/code-history.md#h176 */
    const ST = cfg.style || '';
    // 1) BSP로 방을 뽑는다
    const all: Bag[] = [];
    this.bspSplit(x0, y0, w, h, cfg.depth || 4, minW, minH, rng, all);
    /* 2) 도면(plan)이 있으면 그 칸에 든 방만 남긴다. */
    let leaves = all;
    const plan = cfg.plan && RUIN_PLANS[cfg.plan];
    /* 삼각형(plan 'tri' — 피라미드). */
    const tri = cfg.plan === 'tri';
    const inTri = (x: number, y: number) => y >= y0 && y < y0 + h &&
      Math.abs(x + 0.5 - (x0 + w / 2)) <= (y - y0 + 1) * (w / 2) / h;
    if (tri) {
      /* ★ 피라미드는 BSP 로 자르지 않고 **층**으로 쌓는다. */
      const rooms = [];
      const mid = x0 + w / 2;
      const top0 = y0 + 7;
      // 낡은 피라미드는 층을 높이고(10) 왕의 방을 큰 홀로 — 낮은 방 여러 층이 '방이 많다'로만 읽혔다
      const LH = cfg.worn ? 10 : 8;
      for (let by = top0; by + LH <= y0 + h; by += LH) {
        const last = by + LH * 2 > y0 + h;
        const bh = last ? (cfg.worn ? Math.min(16, y0 + h - by - 1) : Math.min(12, y0 + h - by)) : LH;
        const half = Math.floor((by - y0) * (w / 2) / h) - 2;  // 이 층 **윗줄** 폭 — 가장 좁은 자리
        if (half < 5) continue;
        let xl = Math.ceil(mid - half), xr = Math.floor(mid + half);
        const row = [];
        if (last) {                                            // 왕의 방을 가운데에 먼저
          const kw = Math.min(cfg.worn ? 28 : 22, xr - xl + 1);
          const kx = Math.round(mid - kw / 2);
          row.push({ x: kx, y: by, w: kw, h: bh, grand: cfg.worn ? 1 : 0 });
          for (let x = kx - 1; x - 8 >= xl;) { const rw = Math.min(rng.int(9, 13), x - xl + 1); row.push({ x: x - rw + 1, y: by, w: rw, h: bh }); x -= rw; }
          for (let x = kx + kw; x + 8 <= xr;) { const rw = Math.min(rng.int(9, 13), xr - x + 1); row.push({ x, y: by, w: rw, h: bh }); x += rw; }
        } else {
          /* ★ 좁은 곁방과 넓은 전실이 섞여야 무덤의 방 배치로 읽힌다. */
          for (let x = xl; x + 6 <= xr;) {
            let rw = rng.chance(0.3) ? rng.int(14, 18) : rng.int(7, 10);
            if (cfg.worn) rw += 1;
            if (xr - (x + rw) + 1 < 7) rw = xr - x + 1;       // 남는 폭이 방 하나가 안 되면 붙인다
            row.push({ x, y: by, w: rw, h: bh }); x += rw;
          }
        }
        for (const r of row)
          if (inTri(r.x - 1, r.y - 1) && inTri(r.x + r.w, r.y - 1) &&
              inTri(r.x - 1, r.y + r.h) && inTri(r.x + r.w, r.y + r.h)) rooms.push(r);
      }
      if (rooms.length >= 3) leaves = rooms;
      // 낡은 피라미드도 방 수 바닥(목표)을 지킨다 — 층 안에서 가장 넓은 방을 세로로 반 가른다(층 줄은 그대로)
      for (let guard = 0; cfg.worn && leaves.length < (cfg.target || 0) && guard < 30; guard++) {
        const r = leaves.filter(q => !q.grand && q.w >= 16).sort((p, q) => q.w - p.w)[0];
        if (!r) break;
        const half = r.w >> 1;
        leaves.splice(leaves.indexOf(r), 1, { x: r.x, y: r.y, w: half, h: r.h }, { x: r.x + half, y: r.y, w: r.w - half, h: r.h });
      }
    } else if (plan) {
      const rows = plan.length, cols = plan[0].length;
      const inPlan = (r: any) => {
        const cxr = clamp(Math.floor((r.x + r.w / 2 - x0) / w * cols), 0, cols - 1);
        const cyr = clamp(Math.floor((r.y + r.h / 2 - y0) / h * rows), 0, rows - 1);
        return plan[cyr][cxr] !== '.';
      };
      const kept = all.filter(inPlan);
      if (kept.length >= 3) leaves = kept;
    }
    /* 2.4) 낡은 유적 — **큰 홀 자리를 먼저 떼어 둔다.** 둘레 방은 홀에 깎인 나머지만 남긴다(너무 얇은 조각은 암반).
       BSP 는 납작한 방을 좋아해서, 자라게 두면 높은 홀이 안 나온다. 사연: docs/code-history.md#h167 */
    if (cfg.worn && !cfg.maze && !tri && leaves.length > 4) {
      const HW = ST === 'wide' ? clamp(Math.round(w * 0.42), 34, 52) : clamp(Math.round(w * 0.4), 28, 40);
      const HH = ST === 'wide' ? clamp(Math.round(h * 0.28), 11, 13) : clamp(Math.round(h * 0.4), 16, 22);
      const ok = (r: Bag) => !plan || [[0, 0], [1, 0], [0, 1], [1, 1], [0.5, 0.5]].every(([fx, fy]) => {
        const c = clamp(Math.floor((r.x + r.w * fx * 0.98 - x0) / w * plan[0].length), 0, plan[0].length - 1);
        const q = clamp(Math.floor((r.y + r.h * fy * 0.98 - y0) / h * plan.length), 0, plan.length - 1);
        return plan[q][c] !== '.';
      });
      const xs = [0.5, 0.3, 0.7, 0.15, 0.85], ys = [1, 0.5, 0];
      let hall: Bag | null = null;
      for (const fy of ys) { for (const fx of xs) {
        const r = { x: Math.round(x0 + 1 + (w - 2 - HW) * fx), y: Math.round(y0 + 1 + (h - 2 - HH) * fy), w: HW, h: HH };
        if (ok(r)) { hall = r; break; }
      } if (hall) break; }
      if (hall) {
        const next: Bag[] = [];
        for (const r of leaves) {
          const ox = Math.min(r.x + r.w, hall.x + hall.w) - Math.max(r.x, hall.x), oy = Math.min(r.y + r.h, hall.y + hall.h) - Math.max(r.y, hall.y);
          if (ox <= 0 || oy <= 0) { next.push(r); continue; }
          // 홀을 뺀 나머지 — 위 · 아래 띠는 방 너비 그대로, 옆 조각은 홀 높이만큼
          const ty = Math.max(r.y, hall.y), by = Math.min(r.y + r.h, hall.y + hall.h);
          const parts = [{ x: r.x, y: r.y, w: r.w, h: hall.y - r.y }, { x: r.x, y: hall.y + hall.h, w: r.w, h: r.y + r.h - hall.y - hall.h },
            { x: r.x, y: ty, w: hall.x - r.x, h: by - ty }, { x: hall.x + hall.w, y: ty, w: r.x + r.w - hall.x - hall.w, h: by - ty }];
          for (const p of parts) if (p.w >= 9 && p.h >= 8) next.push(p);
        }
        hall.grand = 1; hall.hall = 1;
        next.push(hall);
        leaves = next;
      }
    }
    /* 2.45) 낡은 유적 — 이웃 방을 삼켜 **큰 홀 하나 · 곁채 둘**로 키운다(작은 방이 고르게 깔리면 '방이 많다'로만 읽혔다).
       방 수를 맞추기(2.5 · 골방) **전에** 한다 — 뒤에 하면 다 맞춘 방을 도로 삼켜 광산이 8칸이 됐다.
       난수를 쓰지 않는다 — 고르는 순서가 정해져 있어 뒤따르는 생성이 밀리지 않는다. 사연: docs/code-history.md#h167 */
    if (cfg.worn && !cfg.maze && !tri && leaves.length > 4 && !leaves.some(r => r.grand)) {
      const cx = x0 + w / 2, cy = y0 + h * 0.6;
      const near = (r: Bag) => Math.abs(r.x + r.w / 2 - cx) + Math.abs(r.y + r.h / 2 - cy) * 1.5;
      const mW = Math.min(44, w * 0.55), mH = Math.min(22, h * 0.6);
      // 가운데 가까운 후보 여섯을 미리 키워 보고 가장 크게 자라는 것을 고른다(합칠 수 있는 이웃은 자리마다 다르다)
      let grand = leaves[0], gs = -1e9;
      for (const c of leaves.slice().sort((a, b) => near(a) - near(b)).slice(0, 6)) {
        const copy = leaves.map(q => ({ ...q })), hc = copy[leaves.indexOf(c)];
        this._growHall(copy, hc, 30, 16, mW, mH);
        const sc = Math.min(1, hc.w / 30) + Math.min(1, hc.h / 16) - near(c) * 0.004;
        if (sc > gs) { gs = sc; grand = c; }
      }
      this._growHall(leaves, grand, 30, 16, mW, mH);
      grand.grand = 1; grand.hall = 1; grand.cell = 0;
    }
    if (cfg.worn && !cfg.maze && !tri && leaves.length > 4) {
      for (let k = 0; k < 2; k++) {
        const left = k === 0;
        const wing = leaves.filter(r => !r.hall && r.w * r.h >= 100)
          .sort((a, b) => (left ? a.x - b.x : b.x + b.w - a.x - a.w) || b.w * b.h - a.w * a.h)[0];
        if (!wing) break;
        if (ST === 'wide') this._growHall(leaves, wing, 28, 8, 40, 10);
        else this._growHall(leaves, wing, 20, 12, 30, 16);
        wing.hall = 1; wing.cell = 0;
      }
    }
    /* 2.5) 방 수를 목표에 맞춘다 — 모자라면 **가장 넓은 방부터 한 번 더 자른다.** */
    const target = tri ? 0 : (cfg.target || 0);                // 피라미드는 층이 곧 방 수다
    if (target) {
      const splittable = (r: any) => r.h >= minH * 2 + 1 || r.w >= minW * 2 + 1;
      let guard = 0;
      while (leaves.length < target && guard++ < 400) {
        let best = null;
        for (const r of leaves)
          if (splittable(r) && !r.hall && (!best || r.w * r.h > best.w * best.h)) best = r;
        if (!best) break;
        const two: Bag[] = [];
        this.bspSplit(best.x, best.y, best.w, best.h, 1, minW, minH, rng, two);
        if (two.length < 2) break;
        leaves.splice(leaves.indexOf(best), 1, ...two);
      }
    }
    /* 2.6) 방 크기를 **눈에 띄게** 가른다 — 큰 홀 · 보통 방 · 골방. */
    if (target && rng) {
      const halls = cfg.worn ? 0 : Math.max(1, Math.round(leaves.length * 0.10));   // 낡은 유적은 큰 홀 · 곁채가 이미 있다
      for (let k = 0; k < halls; k++) {
        const pairs = [];
        for (const a of leaves) for (const b of leaves)
          if (a !== b && !a.hall && !b.hall && !a.cell && !b.cell && a.y === b.y && a.h === b.h &&
              a.x + a.w === b.x && a.w + b.w <= 46) pairs.push([a, b]);
        if (!pairs.length) break;
        const [a, b] = rng.pick(pairs);
        leaves.splice(leaves.indexOf(b), 1);
        a.w += b.w; a.hall = 1;
      }
      const cw = Math.max(7, minW - 4);                 // 골방 가로(벽 포함) — 안쪽 다섯 칸
      /* 골방은 목표 방 수의 **바닥을 지키는** 몫도 한다 — 2.5) 가 최소 크기에 걸려 목표에 못 미치면(실측 d1 석판 2: 11/14) 모자란 만큼 더 가른다. */
      const cells = Math.max(Math.round(leaves.length * (cfg.worn ? 0.12 : 0.22)), target - leaves.length);
      for (let k = 0; k < cells || leaves.length < target; k++) {
        /* 세로로 가를 폭이 없으면 **가로로**, 위쪽에 낮은 다락(높이 6)을 떼어 낸다. */
        const cand = leaves.filter(r => !r.hall && !r.cell && (r.w >= cw + minW || r.h >= minH + 7));
        if (!cand.length) break;
        const r = rng.pick(cand);
        let a: Bag, b: Bag;
        if (r.w >= cw + minW) {
          const left = rng.chance(0.5);
          const cut = left ? cw : r.w - cw;
          a = { x: r.x, y: r.y, w: cut, h: r.h }; b = { x: r.x + cut, y: r.y, w: r.w - cut, h: r.h };
          (left ? a : b).cell = 1;
        } else {
          a = { x: r.x, y: r.y, w: r.w, h: 7 }; b = { x: r.x, y: r.y + 7, w: r.w, h: r.h - 7 };
          a.cell = 1;
        }
        leaves.splice(leaves.indexOf(r), 1, a, b);
      }
      /* 그래도 모자라면(낡은 유적 — 최소 15칸) 가장 넓은 방을 반으로 — 반쪽이 8×8 은 남게. 곁채는 보통 방이 다 떨어졌을 때만. */
      for (let guard = 0; cfg.worn && leaves.length < target && guard < 60; guard++) {
        const ok = (r: Bag) => !r.grand && !r.cell && (r.w >= 16 || r.h >= 16);
        const pool = leaves.filter(r => ok(r) && !r.hall);
        const r = (pool.length ? pool : leaves.filter(ok)).sort((p, q) => q.w * q.h - p.w * p.h)[0];
        if (!r) break;
        const vert = r.w >= 16 && (r.w * 0.8 >= r.h || r.h < 16);
        const half = vert ? r.w >> 1 : r.h >> 1;
        const a: Bag = vert ? { x: r.x, y: r.y, w: half, h: r.h } : { x: r.x, y: r.y, w: r.w, h: half };
        const b: Bag = vert ? { x: r.x + half, y: r.y, w: r.w - half, h: r.h } : { x: r.x, y: r.y + half, w: r.w, h: r.h - half };
        leaves.splice(leaves.indexOf(r), 1, a, b);
      }
    }
    /* sparse — 큰 홀 말고는 방을 위 · 양옆으로 줄여 사이에 암반을 남긴다(바닥 줄은 그대로라 굴이 곧게 이어진다). 맞닿지 않으니 6) 이 굴로 잇는다 */
    if (ST === 'sparse')
      for (const r of leaves) {
        if (r.grand) continue;
        const mx = r.w >= 18 ? 3 : r.w >= 12 ? 2 : 0, my = r.h >= 13 ? 3 : r.h >= 10 ? 1 : 0;
        r.x += mx; r.w -= mx * 2; r.y += my; r.h -= my;
      }
    if (tri)                                                   // 삼각형 전체를 먼저 벽돌 덩어리로
      for (let y = y0; y < y0 + h; y++)
        for (let x = x0; x < x0 + w; x++)
          if (inTri(x, y) && this.get(x, y) !== T.BEDROCK) { this.set(x, y, wall); this.setWall(x, y, bg); }
    // 3) 남긴 방들의 자리만 벽으로 채운다 (테두리 한 칸 포함) — 사연: docs/code-history.md#h112
    for (const r of leaves)
      for (let x = r.x - 1; x <= r.x + r.w; x++)
        for (let y = r.y - 1; y <= r.y + r.h; y++) { this.set(x, y, wall); this.setWall(x, y, bg); }
    /* 4) 각 방 속을 판다 (테두리 1칸은 벽으로 남긴다). */
    // 피라미드는 네모와 기둥 홀만 — 둥근 방·팔각 방은 돌을 쌓은 무덤으로 안 읽힌다
    // 낡은 유적은 기둥 홀 대신 네모 — 기둥 사이 세 칸 틈이 위로 길쭉한 구멍으로 보였다(매달린 기둥은 _wearRoom 이 준다)
    const shapes = cfg.shapes || (tri ? ['rect', 'rect', 'rect', cfg.worn ? 'rect' : 'pillars']
                                      : ['rect', 'rect', 'round', 'octagon', cfg.worn ? 'octagon' : 'pillars']);
    for (const r of leaves) {
      // 골방은 좁아서 둥글게 깎으면 걸을 자리가 없다 — 네모로만
      const shape = r.cell ? 'rect' : rng ? rng.pick(shapes) : 'rect';
      if (r.grand) { this._carveGrand(r, wall, floor, bg); continue; }
      r.shape = shape;
      const x1 = r.x + r.w - 1, y1 = r.y + r.h - 1;
      const cx = (r.x + x1) / 2, cy = (r.y + y1) / 2;
      const rx = (r.w - 2) / 2, ry = (r.h - 2) / 2;
      const keepY = y1 - 3;                                  // 이 아래로는 무조건 통로
      for (let x = r.x + 1; x < x1; x++)
        for (let y = r.y + 1; y < y1; y++) {
          let open = true;
          if (y < keepY) {
            if (shape === 'round') {
              const dx = (x - cx) / rx, dy = (y - cy) / ry;
              open = dx * dx + dy * dy <= 1;                 // 타원
            } else if (shape === 'octagon') {
              // 모서리를 비스듬히 잘라 낸다
              const cut = Math.min(r.w, r.h) >> 2;
              const ex = Math.min(x - r.x, x1 - x), ey = Math.min(y - r.y, y1 - y);
              open = ex + ey > cut;
            } else if (shape === 'pillars') {
              // 일정 간격으로 기둥을 남긴다 (천장을 받치는 홀처럼)
              open = !((x - r.x) % 4 === 0 && y > r.y + 1);
            }
          }
          if (open) this.set(x, y, T.AIR);
        }
      for (let x = r.x + 1; x < x1; x++) this.set(x, y1 - 1, floor);
      if (cfg.worn && !r.cell) this._wearRoom(r, wall, keepY);
    }
    // 5) 맞닿은 방끼리 잇는다
    this._tallLink = !!cfg.worn;
    for (let i = 0; i < leaves.length; i++)
      for (let j = i + 1; j < leaves.length; j++) this._linkRooms(leaves[i], leaves[j], floor);
    this._tallLink = false;
    /* 5.5) 피라미드 — 방마다 **아래층과 한 군데는 반드시** 잇는다. */
    if (tri) {
      for (const r of leaves) {
        let best = null, bo = 0;
        for (const q of leaves) {
          if (q.y !== r.y + r.h) continue;
          const o = Math.min(r.x + r.w, q.x + q.w) - Math.max(r.x, q.x);
          if (o > bo) { bo = o; best = q; }
        }
        if (!best || bo < 4) continue;
        const lo = Math.max(r.x, best.x) + 1, hi = Math.min(r.x + r.w, best.x + best.w) - 2;
        const bad = new Set([r.x, r.x + r.w - 2, r.x + r.w - 1, best.x, best.x + best.w - 2, best.x + best.w - 1]);
        const mid0 = (lo + hi) >> 1;
        let dx = -1;
        for (let d = 0; d <= hi - lo && dx < 0; d++)
          for (const c of [mid0 - d, mid0 + d]) if (c >= lo && c <= hi && !bad.has(c)) { dx = c; break; }
        if (dx < 0) continue;
        const walk = best.y + best.h - 3;                    // 아랫방 걷는 줄
        for (let y = r.y + r.h - 2; y <= walk - 2; y++) this.set(dx, y, T.PLATFORM);
      }
    }
    // 6) 그래도 못 들어가는 방이 남으면 직접 굴을 뚫는다.
    this._ensureConnected(x0, y0, w, h, leaves);
    return leaves;
  },

  /** 홀을 키운다 — 이웃 방을 합친 상자가 **방들로 빈틈없이 채워질 때만** 삼킨다(도면 밖 암반을 파지 않게). */
  _growHall(leaves: Bag[], hall: Bag, wantW: number, wantH: number, maxW: number, maxH: number) {
    for (let guard = 0; guard < 16 && (hall.w < wantW || hall.h < wantH); guard++) {
      let best: Bag | null = null, bestS = 1e9;
      for (const n of leaves) {
        if (n === hall || n.hall) continue;
        const bx = Math.min(hall.x, n.x), by = Math.min(hall.y, n.y);
        const bw = Math.max(hall.x + hall.w, n.x + n.w) - bx, bh = Math.max(hall.y + hall.h, n.y + n.h) - by;
        if (bw > maxW || bh > maxH || (bw === hall.w && bh === hall.h)) continue;
        let area = 0, bad = false;
        const mem: Bag[] = [];
        for (const q of leaves) {
          const ox = Math.min(q.x + q.w, bx + bw) - Math.max(q.x, bx), oy = Math.min(q.y + q.h, by + bh) - Math.max(q.y, by);
          if (ox <= 0 || oy <= 0) continue;
          if (ox !== q.w || oy !== q.h || (q !== hall && q.hall)) { bad = true; break; }
          area += q.w * q.h; mem.push(q);
        }
        if (bad || area !== bw * bh) continue;
        // 모자란 쪽으로 자라는 것을 먼저, 그다음 덜 커지는 것
        const need = (hall.w < wantW && bw > hall.w ? -1000 : 0) + (hall.h < wantH && bh > hall.h ? -1000 : 0);
        const s = need + bw * bh;
        if (s < bestS) { bestS = s; best = { bx, by, bw, bh, mem }; }
      }
      if (!best) break;
      for (const q of best.mem) if (q !== hall) leaves.splice(leaves.indexOf(q), 1);
      hall.x = best.bx; hall.y = best.by; hall.w = best.bw; hall.h = best.bh;
    }
  },

  /** 큰 홀 — 모서리를 크게 깎은 둥근 천장 · 뒷벽의 기둥 줄 · 양쪽 벽에 층층이 붙은 회랑 발판. ★ 난수를 쓰지 않는다. */
  _carveGrand(r: Bag, wall: number, floor: number, bg: number) {
    const x1 = r.x + r.w - 1, y1 = r.y + r.h - 1, fy = y1 - 2, keepY = y1 - 3;
    const cut = Math.min(r.w, r.h) * 0.4 | 0;
    r.shape = 'grand';
    for (let x = r.x + 1; x < x1; x++)
      for (let y = r.y + 1; y < y1; y++) {
        const ex = Math.min(x - r.x, x1 - x), ey = y - r.y;
        // 위 모서리만 둥글게 — 아래(걷는 줄 둘레)는 곧게 둬야 문 · 사다리가 벽에 붙는다
        if (y >= keepY || ex > cut || ey > cut || (ex - cut) ** 2 + (ey - cut) ** 2 <= cut * cut) this.set(x, y, T.AIR);
      }
    for (let x = r.x + 1; x < x1; x++) this.set(x, y1 - 1, floor);
    /* 기둥은 뒷벽(벽지)에만 — 타일로 세우면 홀이 다시 칸으로 쪼개진다. 바닥 위 두 줄은 받침(어두운 벽돌). */
    const n = Math.max(2, Math.round((r.w - 6) / 8)), step = (r.w - 6) / n;
    r.cols = [];
    for (let i = 0; i <= n; i++) {
      const px = Math.round(r.x + 3 + i * step) - 1;
      r.cols.push(px);
      for (let y = r.y + 1; y < y1; y++)
        for (let k = 0; k < 2; k++) if (this.get(px + k, y) === T.AIR) this.setWall(px + k, y, COLUMN_WALL);
    }
    this._wearRoom(r, wall, keepY, true);
    /* 양쪽 벽 회랑 — 바닥에서 한 번에 뛰어 닿는 높이(-2)부터 세 칸 간격. 위층일수록 길게 내밀어 발코니로 읽힌다. */
    for (let t = 0, py = fy - 2; py > r.y + cut - 1 && t < 4; t++, py -= 3) {
      const len = 3 + t * 2;
      for (let i = 0; i < len; i++) for (const x of [r.x + 1 + i, x1 - 1 - i])
        if (this.get(x, py) === T.AIR && this.get(x, py - 1) === T.AIR) this.set(x, py, T.PLATFORM);
    }
  },

  /** 유적 방을 낡게 — 천장 들쭉날쭉 · 무너진 모서리 · 방마다 한 가지 특색.
      ★ 난수를 뽑지 않는다(칸 해시) — 뒤따르는 생성이 밀리지 않게. 머리 위(keepY-2 위)만 바꾼다. */
  _wearRoom(r: Bag, wall: number, keepY: number, plain?: boolean) {
    const hs = (a: number, b: number) => { let v = (a * 374761393 + b * 668265263) | 0; v = (v ^ (v >>> 13)) * 1274126177; return ((v ^ (v >>> 16)) >>> 0); };
    const x1 = r.x + r.w - 1, lim = keepY - 2;
    const fill = (x: number, y: number) => { if (y <= lim && y > r.y && x > r.x && x < x1) this.set(x, y, wall); };
    /* 작은 방은 덜 무너뜨린다 — 천장 두 줄 · 모서리 넉 칸이 높이 9 · 폭 10 아래 방을 굴처럼 짓눌렀다. 사연: docs/code-history.md#h177 */
    const dMax = r.h >= 12 ? 2 : r.h >= 9 ? 1 : 0, big = r.w >= 14 && r.h >= 11;
    let d = Math.min(dMax, hs(r.x, r.y) % 2);
    for (let x = r.x + 1; x < x1; x++) {                    // 천장 — 이웃 칸과 한 칸 넘게 안 벌어진다
      const t = hs(r.x * 7 + ((x - r.x) / 4 | 0), r.y) % 3;     // 네 칸씩 묶어야 톱니로 안 보인다
      d = Math.max(0, Math.min(dMax, t > d ? d + 1 : t < d ? d - 1 : d));
      for (let y = r.y + 1; y <= r.y + d; y++) fill(x, y);
    }
    const s0 = big ? 2 + hs(r.x, r.h) % 3 : 1, s1 = big ? 2 + hs(x1, r.w) % 3 : 1;      // 무너진 위 모서리
    for (let i = 0; i < 5; i++) for (let j = 0; j < 5; j++) {
      if (i + j < s0) fill(r.x + 1 + i, r.y + 1 + j);
      if (i + j < s1) fill(x1 - 1 - i, r.y + 1 + j);
    }
    /* ★ 위로 길쭉한 틈(폭 2칸 이하)은 위에서부터 메운다 — 천장 홈 · 모서리 사이에 좁은 굴뚝이 남았다. */
    for (let y = r.y + 1; y <= lim; y++)
      for (let x = r.x + 1; x < x1;) {
        if (this.get(x, y) !== T.AIR) { x++; continue; }
        let e = x; while (e < x1 && this.get(e, y) === T.AIR) e++;
        if (e - x <= 2) for (let i = x; i < e; i++) fill(i, y);
        x = e;
      }
    if (r.w < 12 || plain) return;
    const k = hs(r.x + r.w, r.y + r.h) % 4;
    if (k <= 1) {                                            // 부서져 매달린 기둥 (0 하나 · 1 둘)
      const xs = k ? [r.x + (r.w / 3 | 0), x1 - (r.w / 3 | 0)] : [r.x + (r.w >> 1) + (hs(r.y, r.x) % 5) - 2];
      for (const px of xs) {
        const len = lim - r.y - (hs(px, r.y) % 2);
        for (let y = r.y + 1; y < r.y + len; y++) { fill(px, y); if (y < r.y + len - 1) fill(px + 1, y); }
      }
    } else if (k === 2 && lim - r.y >= 5) {                  // 한쪽 벽에 남은 회랑 발판
      const left = hs(r.w, r.h) % 2 === 0, n = Math.max(3, r.w / 3 | 0);
      for (let i = 0; i < n; i++) { const x = left ? r.x + 1 + i : x1 - 1 - i; if (this.get(x, lim) === T.AIR) this.set(x, lim, T.PLATFORM); }
    }
  },

  /** 방 하나에서 걸어 닿을 수 있는 칸을 모아 온다 */
  _walkable(x0: any, y0: any, w: any, h: any, sx: any, sy: any) { const { WW } = this.dims;
    const seen = new Set(), st = [[sx, sy]];
    seen.add(sy * WW + sx);
    while (st.length) {
      const [x, y] = st.pop()!;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx, ny = y + dy;
        if (nx < x0 - 1 || nx > x0 + w || ny < y0 - 1 || ny > y0 + h) continue;
        const k = ny * WW + nx;
        if (seen.has(k) || TILE_DEF[this.get(nx, ny)].solid === 1) continue;
        seen.add(k); st.push([nx, ny]);
      }
    }
    return seen;
  },

  /** 고립된 방마다 가장 가까운 이미 닿는 방까지 ㄱ자 굴을 판다 */
  _ensureConnected(x0: number, y0: number, w: any, h: number, rooms: any) { const { WW } = this.dims;
    const spot = (r: any) => [r.x + 2, r.y + r.h - 3];
    let guard = 0;
    while (guard++ < rooms.length + 2) {
      const [bx, by] = spot(rooms[0]);
      const seen = this._walkable(x0, y0, w, h, bx, by);
      const lost = rooms.filter((r: any) => { const [sx, sy] = spot(r); return !seen.has(sy * WW + sx); });
      if (!lost.length) return;
      // 닿는 방 중 가장 가까운 것과 잇는다
      const ok = rooms.filter((r: any) => { const [sx, sy] = spot(r); return seen.has(sy * WW + sx); });
      const a = lost[0];
      let best = ok[0], bd = 1e9;
      for (const r of ok) {
        const d = Math.abs(r.x - a.x) + Math.abs(r.y - a.y);
        if (d < bd) { bd = d; best = r; }
      }
      const [ax, ay] = spot(a), [tx, ty] = spot(best);
      /* 잠긴 돌(암호석·봉인석)은 뚫지 않는다 — 뚫으면 자물쇠가 무의미해진다 */
      const lk = (x: number, y: number) => this.locked(x, y);
      const dig = (x: number, y: number) => { if (!lk(x, y)) this.set(x, y, T.AIR); };
      // 세로로 먼저 파고 (발판을 놓아 올라갈 수 있게) 가로로 잇는다
      const y1 = Math.min(ay, ty), y2 = Math.max(ay, ty);
      for (let y = y1; y <= y2; y++) {
        dig(ax, y); dig(ax, y - 1);
        if (y % 3 === 0 && !lk(ax, y)) this.set(ax, y, T.PLATFORM);
      }
      const xa = Math.min(ax, tx), xb = Math.max(ax, tx);
      for (let x = xa; x <= xb; x++) { dig(x, y1); dig(x, y1 - 1); }
    }
  },

  /* ---- 걸어서 닿는지 검사하고 고친다 ---- */

  /** 한 자리에서 뛰어서 닿는 "설 수 있는 칸"을 모아 온다 */
  /** 걸음 판정에 쓰는 칸 물음들. */
  _standFns() {
    const sup = (x: number, y: number) => { const s = TILE_DEF[this.get(x, y)].solid; return s === 1 || s === 2; };
    /* ★ 잠긴 돌(암호석·봉인석)은 **지나갈 수 있는 것으로** 본다 — 풀면 열리는 문이다 (tools/ruindiag.py 도 그렇게 잰다). */
    const free = (x: number, y: number) => TILE_DEF[this.get(x, y)].solid !== 1 || this.locked(x, y);
    const liq = (x: number, y: number) => !!TILE_DEF[this.get(x, y)].liquid;  // 물속에서는 뜬다 (Ent.move)
    const body = (x: number, y: number) => free(x, y) && free(x, y - 1);      // 키 두 칸이 들어가는가
    const stand = (x: number, y: number) => body(x, y) && (sup(x, y + 1) || liq(x, y));
    return { free, body, stand };
  },
  /** (sx, sy) 에 떨어뜨린 몸이 처음 발을 딛는 칸 — 없으면 null */
  _standSeed(box: Bag, f: any, sx: number, sy: number) {
    for (let cy = sy; cy <= box[3]; cy++) {
      if (!f.body(sx, cy)) break;
      if (f.stand(sx, cy)) return [sx, cy];
    }
    for (let k = 1; k <= 6; k++) if (f.stand(sx, sy - k)) return [sx, sy - k];
    return null;
  },
  /** (x, y) 에 선 몸이 **한 번에** 옮겨 설 수 있는 칸마다 push(nx, ny) — 떨어지기 · 제자리 점프 · 옆으로 한 번에 네 칸까지 뛰어 떨어지기 · 발판 뚫고 내려가기. */
  _standNext(box: Bag, f: any, x: number, y: number, push: any) {
    const JUMP = 3, RUN = 4;                                  // 오를 수 있는 높이 · 한 번에 나는 폭
    const drop = (x: number, y: number) => {                                  // 발이 닿을 때까지 떨어진다
      for (let cy = y; cy <= box[3]; cy++) {
        if (!f.body(x, cy)) return;
        if (f.stand(x, cy)) { push(x, cy); return; }
      }
    };
    if (TILE_DEF[this.get(x, y + 1)].solid === 2) drop(x, y + 2);   // 발판을 뚫고 내려간다
    for (let h = 0; h <= JUMP; h++) {
      const yh = y - h;
      if (yh <= box[1]) break;
      if (h > 0 && !f.free(x, yh - 1)) break;                 // 머리가 천장에 막힌다
      if (h > 0 && f.stand(x, yh)) push(x, yh);               // 제자리 점프로 발판에 올라선다
      for (const dx of [-1, 1]) for (let s = 1; s <= RUN; s++) {
        const nx = x + dx * s;
        if (nx < box[0] || nx > box[2] || !f.body(nx, yh)) break;
        drop(nx, yh);
      }
    }
  },
  /** (sx, sy) 에서 걸어서(뛰고 떨어지며) 닿는 설 자리 전부. */
  _standSet(box: Bag, sx: number, sy: number) { const { WW } = this.dims;
    const f = this._standFns();
    const seen = new BoxSet(box as number[], 10, WW), st: number[] = [];
    const push = (x: number, y: number) => { const k = y * WW + x; if (!seen.has(k)) { seen.add(k); st.push(x, y); } };
    const s0 = this._standSeed(box, f, sx, sy);
    if (s0) push(s0[0], s0[1]);
    let guard = 0;
    while (st.length && guard++ < 200000) {
      const y = st.pop(), x = st.pop();
      this._standNext(box, f, x, y, push);
    }
    return seen;
  },
  /** 거꾸로 걷기 — 상자 안 설 자리 가운데 **rootK 까지 걸어 닿을 수 있는** 칸 전부 — 사연: docs/code-history.md#h113 */
  _returnSet(box: Bag, rootK: any) { const { WW } = this.dims;
    const f = this._standFns();
    const preds = new Map();
    for (let y = box[1] - 8; y <= box[3]; y++)
      for (let x = box[0]; x <= box[2]; x++) {
        if (!f.stand(x, y)) continue;
        const k = y * WW + x;
        this._standNext(box, f, x, y, (nx: number, ny: number) => {
          const nk = ny * WW + nx;
          let a = preds.get(nk);
          if (!a) preds.set(nk, a = []);
          a.push(k);
        });
      }
    const R = new BoxSet(box as number[], 10, WW), st = [rootK];
    R.add(rootK);
    while (st.length) {
      const a = preds.get(st.pop());
      if (a) for (const p of a) if (!R.has(p)) { R.add(p); st.push(p); }
    }
    return R;
  },

  /** 두 자리를 걸어 다닐 수 있게 잇는다 — 가로 굴을 내고 세로로 발판 사다리를 세운다 */
  _digStair(ax: number, ay: number, tx: number, ty: number, floor: number, box: Bag, traps: any, rng: any) {
    const yT = Math.min(ay, ty), yB = Math.max(ay, ty);
    const c = clamp(tx, box[0] + 1, box[2] - 2);
    /* ★ 이미 놓인 발판은 절대 지우지 않는다. */
    /* 발판은 지우지 않고, **잠긴 돌(암호석·봉인석)도 건드리지 않는다.** */
    const locked = (x: number, y: number) => this.locked(x, y);
    const bore = (x: number, y: number) => {
      if (locked(x, y) || TILE_DEF[this.get(x, y)].solid === 2) return;
      this.set(x, y, T.AIR);
    };
    // ① a 자리 높이에서 t 자리 열까지 가로 굴.
    const rx0 = clamp(Math.min(ax, c) - 1, box[0], box[2]);
    const rx1 = clamp(Math.max(ax, c) + 2, box[0], box[2]);
    for (let x = rx0; x <= rx1; x++) {
      for (let k = 0; k < 3; k++) bore(x, ay - k);
      const s = TILE_DEF[this.get(x, ay + 1)].solid;
      if (s === 0 && !locked(x, ay + 1)) this.set(x, ay + 1, floor);   // 발판(2)이면 그대로 둔다
    }
    // ② t 자리 열에서 위아래를 잇는 두 칸 폭 수직굴.
    const plat = (x: number, y: number) => { if (!locked(x, y)) this.set(x, y, T.PLATFORM); };
    for (let y = yT - 2; y <= yB; y++) { bore(c, y); bore(c + 1, y); }
    for (let y = yB - 2; y > yT; y -= 3) { plat(c, y); plat(c + 1, y); }
    if (yB - yT >= 2) { plat(c, yT + 1); plat(c + 1, yT + 1); }
    if (TILE_DEF[this.get(c, yB + 1)].solid === 0 && !locked(c, yB + 1)) {
      this.set(c, yB + 1, floor); this.set(c + 1, yB + 1, floor);
    }
    /* ★ 이 계단에도 함정을 하나 심는다 — 주인 방은 빼고(둥지 옆 가시가 결전 내내 찔렀다). 사연: docs/code-history.md#h168 */
    const tx0 = Math.round((ax + c) / 2);
    if (rng && !this._inBossRoom(tx0, ay)) this.putPathTrap(tx0, ay, rng);
  },

  /** 유적 주인 방(석판 방) 안인가 — 방 목록은 큰 홀 · 가장 넓은 방이 맨 앞이다 */
  _inBossRoom(x: number, y: number) {
    for (const s of this.ruinSites || []) {
      const b = (s.rooms || [])[0];
      if (b && x >= b.x - 1 && x <= b.x + b.w && y >= b.y - 1 && y <= b.y + b.h) return true;
    }
    return false;
  },

  /** spots 의 모든 자리를 서로 걸어 다닐 수 있게 만든다. */
  _ensureWalkable(x0: any, y0: any, w: any, h: any, spots: any, floor: any, traps: any, rng: any) { const { WW, WH } = this.dims;
    if (spots.length < 2) return;
    // 검사 범위는 유적 둘레 열두 칸.
    /* ★ 옆으로도 같다 — 입구 통로는 유적 옆 바깥 열(x0-5)에서 좌우로 서른여섯 칸까지 오르내리며 내려오므로, 통로의 방이 상자(유적 ±12) 밖에 놓일 수 있다. */
    const top = spots.reduce((m: number, p: number[]) => Math.min(m, p[1] - 3), y0 - 12);
    const left = spots.reduce((m: number, p: number[]) => Math.min(m, p[0] - 6), x0 - 12);
    const right = spots.reduce((m: number, p: number[]) => Math.max(m, p[0] + 6), x0 + w + 12);
    const box = [Math.max(2, left), Math.max(2, top),
                 Math.min(WW - 3, right), Math.min(WH - 3, y0 + h + 12)];
    /* 오르내림은 대칭이 아니다 — 떨어지는 것은 공짜지만 올라오는 데는 발판이 있어야 한다. */
    const d0 = (p: number[]) => Math.abs(p[0] - spots[0][0]) + Math.abs(p[1] - spots[0][1]);
    const far = spots.reduce((b: any, p: any) => (d0(p) > d0(b) ? p : b), spots[1]);
    for (const anchor of [spots[0], far]) this._walkPass(box, anchor, spots, floor, traps, rng);
    /* 가는 길과 오는 길을 번갈아 손본다. */
    this._walkBack(box, spots, floor, traps, rng);
    this._walkPass(box, spots[0], spots, floor, traps, rng);
    this._walkBack(box, spots, floor, traps, rng);
  },

  /** 자리마다 **기준점으로 돌아올 수 있는지** 하나씩 걸어 보고, 못 돌아오면 길을 낸다. */
  _walkBack(box: Bag, spots: any, floor: number, traps: any, rng: any) { const { WW } = this.dims;
    const near = (set: any, tx: number, ty: number) => {
      let b = null, bd = 1e9;
      for (const k of set) {
        const y = Math.floor(k / WW), x = k - y * WW;
        const d = Math.abs(x - tx) + Math.abs(y - ty);
        if (d < bd) { bd = d; b = [x, y]; }
      }
      return b;
    };
    /* 되돌아올 수 있는지는 "기준점 칸을 밟는가"가 아니라 **기준점에서 걸어 닿는 무리와 한 칸이라도 겹치는가**로 본다. */
    let home = this._standSet(box, spots[0][0], spots[0][1]);
    let root = home.values().next().value;                    // 기준점에서 실제로 발을 딛는 칸
    if (root === undefined) return;
    const rx = root % WW, ry = Math.floor(root / WW);
    const f = this._standFns();
    let R = this._returnSet(box, root);                       // root 로 걸어 돌아올 수 있는 칸 (_returnSet 의 ★)
    /* ★ 여기서 "빠른 길"을 쓰면 안 된다. */
    for (let i = 1; i < spots.length; i++) {
      if (spots[i][2] === 0) continue;                        // 같은 방의 곁자리는 건너뛴다
      for (let k = 0; k < 4; k++) {                           // 한 번에 안 되면 몇 번 더 잇는다
        const s0 = this._standSeed(box, f, spots[i][0], spots[i][1]);
        if (!s0) break;                                       // 설 자리가 없다(예전 back.size === 0)
        if (R.has(s0[1] * WW + s0[0])) break;                 // 돌아올 수 있다(예전 back.has(root))
        const back = this._standSet(box, spots[i][0], spots[i][1]);
        /* 나오는 쪽에서 기준점에 가장 가까운 칸(a)과, **거기서 아직 못 가는** 쪽 칸(b)을 잇는다. */
        const a = near(back, rx, ry);
        let b = null, bd = 1e9;
        for (const kk of home) {
          if (back.has(kk)) continue;
          const y = Math.floor(kk / WW), x = kk - y * WW;
          const d = Math.abs(x - a![0]) + Math.abs(y - a![1]);
          if (d < bd) { bd = d; b = [x, y]; }
        }
        if (!a || !b) break;
        this._digStair(a[0], a[1], b[0], b[1], floor, box, traps, rng);
        home = this._standSet(box, spots[0][0], spots[0][1]);
        root = home.values().next().value;
        R = this._returnSet(box, root);                       // 판 굴로 길이 바뀌었다 — 다시 잰다
      }
    }
  },

  _walkPass(box: Bag, anchor: any, spots: any, floor: number, traps: any, rng: any) { const { WW } = this.dims;
    // 넉넉하게 잡으면 "닿은 칸 옆"을 닿았다고 세어 버린다 — 바짝 붙여 본다
    const hit = (seen: any, p: number[]) => {
      for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 2; dy++)
        if (seen.has((p[1] + dy) * WW + p[0] + dx)) return true;
      return false;
    };
    let guard = 0;
    const tried = new Map();                                  // 자리마다 틈 잇기를 몇 번 해 봤나
    while (guard++ < spots.length * 2 + 4) {                  // 한 번에 한 자리씩 잇는다
      const seen = this._standSet(box, anchor[0], anchor[1]);
      if (!seen.size) return;
      const lost = spots.filter((p: any) => !hit(seen, p));
      if (!lost.length) return;
      const a = lost[0];
      /* 틈 잇기는 **한 자리에 여섯 번까지** 해 본다 — 사연: docs/code-history.md#h114 */
      const key = a[1] * WW + a[0];
      const once = (tried.get(key) || 0) < 6;
      tried.set(key, (tried.get(key) || 0) + 1);
      /* ★ 굴은 **두 무리 사이의 틈**에 판다 — 기준점에서 닿는 무리(seen)와, 잃은 자리에서 닿는 무리(from) 중 서로 가장 가까운 두 칸을 잇는다 — 사연:
         docs/code-history.md#h115 */
      const from = once ? this._standSet(box, a[0], a[1]) : new Set();
      let src = a, best = null, bd = 1e9;
      if (from.size) {
        const cols = new Map();                               // seen 을 열별로 묶어 가까운 열만 본다
        for (const k of seen) {
          const y = Math.floor(k / WW), x = k - y * WW;
          const c = cols.get(x); if (c) c.push(y); else cols.set(x, [y]);
        }
        /* 두 무리가 겹치는 칸은 뺀다 — 걸음은 한쪽으로만 흐를 수 있어서, 목에서 떨어져 닿는 칸은 맨 아래 방에서도 닿는다. */
        for (const k of from) {
          if (seen.has(k)) continue;
          const fy = Math.floor(k / WW), fx = k - fy * WW;
          for (let dx = 0; dx < bd; dx++) {
            for (const x of dx ? [fx - dx, fx + dx] : [fx]) {
              const c = cols.get(x); if (!c) continue;
              for (const y of c) {
                const d = dx + Math.abs(y - fy);
                if (d < bd) { bd = d; best = [x, y]; src = [fx, fy]; }
              }
            }
          }
        }
      }
      /* ★ 굴은 **실제로 닿는 칸**에서 시작해야 한다. */
      if (!best) {
        for (const k of seen) {
          const y = Math.floor(k / WW), x = k - y * WW;
          const d = Math.abs(x - a[0]) + Math.abs(y - a[1]);
          if (d < bd) { bd = d; best = [x, y]; }
        }
      }
      this._digStair(src[0], src[1], best![0], best![1], floor, box, traps, rng);
    }
  },
};
mixin(World.prototype, WorldDungeon, true);
